/**
 * Timezone-aware date helpers. Callers pass the target timezone string,
 * typically sourced from userManager.user.timezone (which looks like
 * 'US/Central', 'US/Eastern', or the canonical form 'America/Chicago' —
 * both formats are accepted). When `timezone` is nullish, Intl falls back
 * to the browser's timezone.
 */

/**
 * Return year/month/day parts for a date as they appear in the given timezone.
 * @param {Date|string|number} date
 * @param {string} [timezone]
 * @returns {{year: string, month: string, day: string}}
 */
export const getDatePartsInTimezone = (date, timezone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'numeric',
    timeZone: normalizeTimezone(timezone),
    year: 'numeric'
  }).formatToParts(new Date(date));
  const getPart = (type) => parts.find((part) => part.type === type)?.value;
  const datePartsInTimezone = { day: getPart('day'), month: getPart('month'), year: getPart('year') };
  return datePartsInTimezone;
};

/**
 * Same wall-clock day in the given timezone (DST-aware).
 * @param {Date|string|number} dateA
 * @param {Date|string|number} dateB
 * @param {string} [timezone]
 * @returns {boolean}
 */
export const isSameDayInTimezone = (dateA, dateB, timezone) => {
  const a = getDatePartsInTimezone(dateA, timezone);
  const b = getDatePartsInTimezone(dateB, timezone);
  const isSameDay = a.year === b.year && a.month === b.month && a.day === b.day;
  return isSameDay;
};

/**
 * DST-aware short abbreviation (CST/CDT/EST/EDT/PST/PDT/...) for the moment.
 * @param {Date|string|number} [date]
 * @param {string} [timezone]
 * @returns {string|undefined}
 */
export const getTimezoneAbbreviation = (date = new Date(), timezone) => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: normalizeTimezone(timezone),
    timeZoneName: 'short'
  }).formatToParts(new Date(date));
  const timezoneAbbreviation = parts.find((part) => part.type === 'timeZoneName')?.value;
  return timezoneAbbreviation;
};

/**
 * True if a wall-clock date/time is before `now` in the given timezone.
 * The wall-clock strings are compared to `now`'s wall-clock representation
 * in the target timezone.
 *
 * @param {string} dateStr — e.g. "07/23/2026"
 * @param {string} timeStr — e.g. "10:00 PM"
 * @param {Date|string|number} now — usually `Date.now()`
 * @param {string} [timezone]
 * @returns {boolean}
 */
export const isWallClockBeforeInTimezone = (dateStr, timeStr, now, timezone) => {
  const dueLocal = new Date(`${dateStr} ${timeStr}`);
  if (isNaN(dueLocal.valueOf())) {
    return false;
  }
  const due = [
    dueLocal.getFullYear(),
    dueLocal.getMonth() + 1,
    dueLocal.getDate(),
    dueLocal.getHours(),
    dueLocal.getMinutes()
  ];
  const parts = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
    minute: '2-digit',
    month: '2-digit',
    timeZone: normalizeTimezone(timezone),
    year: 'numeric'
  }).formatToParts(new Date(now));
  const getPart = (type) => Number(parts.find((part) => part.type === type)?.value);
  const ref = [getPart('year'), getPart('month'), getPart('day'), getPart('hour'), getPart('minute')];
  for (let i = 0; i < due.length; i++) {
    if (due[i] !== ref[i]) {
      return due[i] < ref[i];
    }
  }
  return false;
};

/**
 * Validate a timezone string. Returns the timezone if Intl accepts it,
 * otherwise undefined (falling back to browser default at usage sites).
 */
const normalizeTimezone = (timezone) => {
  if (!timezone) {
    return undefined;
  }
  try {
    // Constructor throws RangeError for invalid or unrecognized timezone strings.
    // resolvedOptions() call keeps this as an expression rather than a bare `new` statement.
    new Intl.DateTimeFormat('en-US', { timeZone: timezone }).resolvedOptions();
    return timezone;
  } catch (_error) {
    console.warn('[dateTimezone] Invalid timezone, falling back to browser default:', timezone);
    return undefined;
  }
};
